/**
 * Credit Memo Service
 *
 * API client for credit memo operations including:
 * - CRUD operations for credit memos
 * - Credit application to invoices
 * - Customer credit balance queries
 * - Void operations
 *
 * @dependencies
 * - apiClient: Axios-based API client
 */

import apiClient from './apiClient';

const creditMemoService = {
  /**
   * Get all credit memos with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.status - Filter by status
   * @param {number} params.customerId - Filter by customer
   * @param {string} params.search - Search term
   * @param {string} params.startDate - Start date filter
   * @param {string} params.endDate - End date filter
   * @returns {Promise} API response with credit memos and pagination
   */
  getCreditMemos: async (params = {}) => {
    const response = await apiClient.get('/credit-memos', { params });
    return response.data;
  },

  /**
   * Get a specific credit memo by ID
   * @param {number} id - Credit memo ID
   * @returns {Promise} API response with credit memo details
   */
  getCreditMemoById: async (id) => {
    const response = await apiClient.get(`/credit-memos/${id}`);
    return response.data;
  },

  /**
   * Create a new credit memo
   * @param {Object} creditMemoData - Credit memo data
   * @param {number} creditMemoData.customerId - Customer ID
   * @param {number} creditMemoData.amountCents - Amount in cents
   * @param {number} creditMemoData.originalInvoiceId - Original invoice ID (optional)
   * @param {string} creditMemoData.sourceType - Source type (manual, overpayment, return)
   * @param {string} creditMemoData.reason - Reason for credit
   * @param {string} creditMemoData.reasonCode - Reason code (optional)
   * @param {string} creditMemoData.customerNotes - Customer-facing notes (optional)
   * @param {string} creditMemoData.internalNotes - Internal notes (optional)
   * @returns {Promise} API response with created credit memo
   */
  createCreditMemo: async (creditMemoData) => {
    const response = await apiClient.post('/credit-memos', creditMemoData);
    return response.data;
  },

  /**
   * Apply credit memo to an invoice
   * @param {number} creditMemoId - Credit memo ID
   * @param {number} invoiceId - Invoice ID to apply credit to
   * @param {number} amountCents - Amount to apply in cents
   * @returns {Promise} API response with application result
   */
  applyCreditToInvoice: async (creditMemoId, invoiceId, amountCents) => {
    const response = await apiClient.post(`/credit-memos/${creditMemoId}/apply`, {
      invoiceId,
      amountCents
    });
    return response.data;
  },

  /**
   * Void a credit memo (only for credits issued in error with no applications)
   * @param {number} creditMemoId - Credit memo ID
   * @param {string} reason - Void reason
   * @returns {Promise} API response with voided credit memo
   */
  voidCreditMemo: async (creditMemoId, reason) => {
    const response = await apiClient.post(`/credit-memos/${creditMemoId}/void`, {
      reason
    });
    return response.data;
  },

  /**
   * Redeem credit memo (customer receives cash/check refund)
   * @param {number} creditMemoId - Credit memo ID
   * @param {Object} redemptionData - Redemption details
   * @param {number} redemptionData.amountCents - Amount to redeem in cents
   * @param {string} redemptionData.redemptionMethod - Method (cash, check, bank_transfer, other)
   * @param {string} redemptionData.reason - Reason for redemption
   * @param {string} redemptionData.referenceNumber - Optional reference (check number, etc.)
   * @returns {Promise} API response with redemption result
   */
  redeemCredit: async (creditMemoId, redemptionData) => {
    const response = await apiClient.post(`/credit-memos/${creditMemoId}/redeem`, {
      amountCents: redemptionData.amountCents,
      redemptionMethod: redemptionData.redemptionMethod,
      reason: redemptionData.reason,
      referenceNumber: redemptionData.referenceNumber
    });
    return response.data;
  },

  /**
   * Get customer's credit balance and available credits
   * @param {number} customerId - Customer ID
   * @returns {Promise} API response with credit information
   */
  getCustomerCredit: async (customerId) => {
    const response = await apiClient.get(`/credit-memos/customer/${customerId}`);
    return response.data;
  },

  /**
   * Get customer's credit memos
   * @param {number} customerId - Customer ID
   * @param {Object} options - Query options
   * @param {boolean} options.includeApplied - Include fully applied memos
   * @param {boolean} options.includeVoid - Include voided memos
   * @returns {Promise} API response with credit memos
   */
  getCustomerCreditMemos: async (customerId, options = {}) => {
    const params = {
      includeApplied: options.includeApplied || false,
      includeVoid: options.includeVoid || false
    };
    const response = await apiClient.get(`/credit-memos/customer/${customerId}/memos`, { params });
    return response.data;
  }
};

export default creditMemoService;
